import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Button,
  Spin,
  Alert,
  Tag,
  Input,
  Modal,
  Form,
  Select,
  Table,
  Typography,
  Space,
  Tooltip,
} from 'antd'
import {
  ArrowLeftOutlined,
  EditOutlined,
  CopyOutlined,
  CheckOutlined,
  LinkOutlined,
  ReloadOutlined,
  UserOutlined,
  DollarOutlined,
  DollarCircleOutlined,
  ReadOutlined,
  FilePdfOutlined,
  SafetyCertificateOutlined,
  IdcardOutlined,
  PlusOutlined,
  ShopOutlined,
} from '@ant-design/icons'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { useDetalhesStore } from '@/stores/detalhesStore'
import { useAuthStore } from '@/stores/authStore'
import { certificadoPdfUrl, certificadoValidacaoUrl } from '@/api/certificados'
import { linkDaCobranca } from '@/api/pagamentos'
import { MunicipioAutoComplete } from '@/components/MunicipioAutoComplete'
import { TagDeStatus } from '@/components/TagDeStatus'
import { IniciaisDoAluno } from '@/components/IniciaisDoAluno'
import { useEstaEmbutido } from '@/components/Layout'
import { useRotuloDeFilho } from '@/lib/rotuloDeFilho'
import { pedirParaPagarCobrancaNoHyperpay } from '@/lib/hyperpayHost'
import {
  formatDate,
  formatDateTime,
  formatCurrency,
  formatCpf,
  copyToClipboard,
} from '@/lib/formatters'

import { STATUS_LABELS, CNH_CATEGORIES } from '@/lib/constants'
import type { Modulo } from '@/types'

const { Text } = Typography

// ─── Entry component: zero logic, reads like wireframe ───────────────────────

export function Detalhes() {
  return (
    <div className="pb-6 detalhes-container">
      <BarraSuperior />
      <DetalhesContent />
    </div>
  )
}

// ─── Helper components ────────────────────────────────────────────────────────

// Embutido não existe lista de alunos para voltar: a única rota vizinha é a matrícula.
function BarraSuperior() {
  const navigate = useNavigate()
  const matricula = useDetalhesStore((s) => s.matricula)
  const estaEmbutido = useEstaEmbutido()

  return (
    <div className="flex items-center justify-between gap-4 mb-4">
      {estaEmbutido ? (
        <span />
      ) : (
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/alunos')}
          className="!px-0 !text-gray-600"
        >
          Voltar aos alunos
        </Button>
      )}
      {matricula && <TagDeStatus statusDetran={matricula.statusDetran} />}
    </div>
  )
}

function DetalhesContent() {
  const { matriculaId } = useParams<{ matriculaId: string }>()
  const fetchDetalhes = useDetalhesStore((s) => s.fetchDetalhes)
  const loading = useDetalhesStore((s) => s.loading)
  const error = useDetalhesStore((s) => s.error)
  const matricula = useDetalhesStore((s) => s.matricula)

  useEffect(() => {
    if (matriculaId) fetchDetalhes(Number(matriculaId))
  }, [matriculaId])

  if (loading) {
    return (
      <div className="flex justify-center mt-12">
        <Spin size="large" />
      </div>
    )
  }

  if (error) {
    return <Alert description={error} type="error" showIcon />
  }

  if (!matricula) {
    return <Alert description="Matrícula não encontrada." type="error" showIcon />
  }

  return (
    <>
      <CabecalhoDoAluno />
      <div className="detalhes-grid">
        <div className="detalhes-coluna">
          <div className="detalhes-area-dados">
            <DadosPessoaisCard />
          </div>
          <div className="detalhes-area-progresso">
            <ProgressoAcademicoCard />
          </div>
        </div>
        <div className="detalhes-coluna">
          <div className="detalhes-area-identificacao">
            <IdentificacaoCard />
          </div>
          <div className="detalhes-area-cobranca">
            <CobrancaCard />
          </div>
          <div className="detalhes-area-vendedor">
            <VendedorCard />
          </div>
        </div>
      </div>
    </>
  )
}

function CabecalhoDoAluno() {
  const matricula = useDetalhesStore((s) => s.matricula)
  const [editOpen, setEditOpen] = useState(false)

  if (!matricula) return null

  return (
    <div className="detalhes-cabecalho mb-4">
      <div className="detalhes-cabecalho-nova">
        <BotaoNovaMatricula />
      </div>
      <div className="detalhes-cabecalho-aluno flex items-center gap-2 sm:gap-4 min-w-0">
        <IniciaisDoAluno nome={matricula.aluno.nome} tamanho="lg" />
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="!mb-0 text-base sm:text-2xl font-bold text-[#1a3a5c] uppercase break-words">
              {matricula.aluno.nome}
            </h1>
            <IconeCopiar texto={matricula.aluno.nome} />
          </div>
          <Text type="secondary">{matricula.curso.nome}</Text>
        </div>
      </div>
      <Space wrap className="detalhes-cabecalho-acoes">
        <Button icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
          Editar
        </Button>
        <PasswordResetLinkButton alunoId={matricula.alunoId} />
      </Space>
      <EditMatriculaModal open={editOpen} onClose={() => setEditOpen(false)} />
    </div>
  )
}

// Só embutido: ali a matrícula é a tarefa, e o parceiro cai aqui logo depois de criar uma. No painel
// completo o menu já leva a Matricular.
function BotaoNovaMatricula() {
  const navigate = useNavigate()
  const estaEmbutido = useEstaEmbutido()

  if (!estaEmbutido) return null

  return (
    <Button
      type="primary"
      icon={<PlusOutlined />}
      onClick={() => navigate('/embutido/matricula')}
      style={{ background: 'var(--color-accent)', borderColor: 'var(--color-accent)' }}
    >
      Nova Matrícula
    </Button>
  )
}

function DadosPessoaisCard() {
  const matricula = useDetalhesStore((s) => s.matricula)
  if (!matricula) return null
  const { aluno } = matricula

  return (
    <CartaoDeSecao titulo="Dados Pessoais" icone={<UserOutlined />}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
        {/* Copia os dígitos sem máscara: é o formato que os outros sistemas esperam na busca. */}
        <InfoField label="CPF" value={formatCpf(aluno.cpf)} copiavel valorParaCopiar={aluno.cpf} />
        <InfoField label="Data de nascimento" value={formatDate(aluno.dataNascimento)} copiavel />
        <InfoField label="Email" value={aluno.email} copiavel />
        <InfoField label="Telefone" value={aluno.telefone} copiavel />
        <InfoField label="CNH" value={aluno.cnh} copiavel />
        <InfoField label="Categoria" value={matricula.categoriaCnh} copiavel />
        <InfoField label="Município/UF" value={aluno.municipio} copiavel />
        <InfoField label="UF" value={aluno.uf || matricula.uf} copiavel />
        {aluno.rg && <InfoField label="RG" value={aluno.rg} copiavel />}
        <InfoField label="RENACH" value={matricula.renach} copiavel />
        {aluno.dataPrimeiraHabilitacao && (
          <InfoField
            label="Data 1ª habilitação"
            value={formatDate(aluno.dataPrimeiraHabilitacao)}
            copiavel
          />
        )}
      </div>
    </CartaoDeSecao>
  )
}

function IdentificacaoCard() {
  const matricula = useDetalhesStore((s) => s.matricula)
  if (!matricula) return null

  return (
    <div className="bg-[#1a3a5c] text-white rounded-xl p-5">
      <div className="flex items-center gap-2 font-bold mb-4">
        <IdcardOutlined />
        Identificação no Sistema
      </div>
      <div className="text-[11px] uppercase tracking-wide text-white/50 font-medium">
        Nº da matrícula
      </div>
      <div className="font-medium break-all">{matricula.id || '—'}</div>
      <div className="border-t border-white/15 my-4" />
      <div className="text-[11px] uppercase tracking-wide text-white/50 font-medium">
        Data de criação
      </div>
      <div className="font-medium">{formatDate(matricula.createdAt)}</div>
      <div className="border-t border-white/15 my-4" />
      <div className="text-[11px] uppercase tracking-wide text-white/50 font-medium">
        UF da matrícula
      </div>
      <div className="font-medium">{matricula.uf || '—'}</div>
    </div>
  )
}

function PasswordResetLinkButton({ alunoId }: { alunoId: number }) {
  const gerarTokenSenha = useDetalhesStore((s) => s.gerarTokenSenha)
  const [linkSenha, setLinkSenha] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const appUrl = import.meta.env.VITE_APP_URL ?? 'https://app.newdriver.com.br'

  async function handleGerar() {
    setLoading(true)
    try {
      const token = await gerarTokenSenha(alunoId)
      setLinkSenha(`${appUrl}/criar-senha/${token}`)
    } catch {
      toast.error('Erro ao gerar link de senha.')
    } finally {
      setLoading(false)
    }
  }

  if (linkSenha) {
    return (
      <Space.Compact>
        <Input value={linkSenha} readOnly style={{ width: 260 }} />
        <BotaoCopiar texto={linkSenha} />
      </Space.Compact>
    )
  }

  return (
    <Button icon={<LinkOutlined />} loading={loading} onClick={handleGerar}>
      Gerar link de senha
    </Button>
  )
}

function EditMatriculaModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const matricula = useDetalhesStore((s) => s.matricula)
  const updateAluno = useDetalhesStore((s) => s.updateAluno)
  const fetchDetalhes = useDetalhesStore((s) => s.fetchDetalhes)

  const formik = useFormik({
    initialValues: {
      nome: matricula?.aluno.nome ?? '',
      telefone: matricula?.aluno.telefone ?? '',
      email: matricula?.aluno.email ?? '',
      categoriaCnh: matricula?.categoriaCnh ?? '',
      dataPrimeiraHabilitacao: matricula?.aluno.dataPrimeiraHabilitacao ?? '',
      municipio: matricula?.aluno.municipio ?? '',
      renach: matricula?.renach ?? '',
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!matricula) return
      try {
        await updateAluno(matricula.aluno.cpf, { ...values, matriculaId: matricula.id })
        await fetchDetalhes(matricula.id)
        toast.success('Dados atualizados!')
        onClose()
      } catch {
        toast.error('Erro ao atualizar dados.')
      }
    },
  })

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Editar Dados da Matrícula"
      width="80%"
      footer={null}
    >
      <Form layout="vertical" component={false}>
        <form onSubmit={formik.handleSubmit} className="pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            <Form.Item label="CPF">
              <Input value={formatCpf(matricula?.aluno.cpf ?? '')} disabled />
            </Form.Item>
            <Form.Item label="Data de Nascimento">
              <Input value={formatDate(matricula?.aluno.dataNascimento)} disabled />
            </Form.Item>
            <Form.Item label="CNH">
              <Input value={matricula?.aluno.cnh ?? ''} disabled />
            </Form.Item>
            <Form.Item label="UF">
              <Input value={matricula?.uf ?? ''} disabled />
            </Form.Item>

            <Form.Item label="Nome">
              <Input name="nome" value={formik.values.nome} onChange={formik.handleChange} />
            </Form.Item>
            <Form.Item label="E-mail">
              <Input name="email" value={formik.values.email} onChange={formik.handleChange} />
            </Form.Item>
            <Form.Item label="Telefone">
              <Input
                name="telefone"
                value={formik.values.telefone}
                onChange={formik.handleChange}
              />
            </Form.Item>
            <Form.Item label="Categoria CNH">
              <Select
                value={formik.values.categoriaCnh || undefined}
                onChange={(value) => formik.setFieldValue('categoriaCnh', value)}
                options={CNH_CATEGORIES.map((c) => ({ label: c, value: c }))}
                allowClear
                placeholder="Selecione"
              />
            </Form.Item>
            <Form.Item label="Data 1ª Habilitação">
              <Input
                name="dataPrimeiraHabilitacao"
                value={formik.values.dataPrimeiraHabilitacao}
                onChange={formik.handleChange}
                placeholder="DD/MM/AAAA"
              />
            </Form.Item>
            <Form.Item label="RENACH">
              <Input name="renach" value={formik.values.renach} onChange={formik.handleChange} />
            </Form.Item>
            <MunicipioAutoComplete
              value={formik.values.municipio}
              uf={matricula?.uf ?? ''}
              onChange={(municipio) => formik.setFieldValue('municipio', municipio)}
            />
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button onClick={onClose}>Cancelar</Button>
            <Button type="primary" htmlType="submit" loading={formik.isSubmitting}>
              Salvar
            </Button>
          </div>
        </form>
      </Form>
    </Modal>
  )
}

function CobrancaCard() {
  const matricula = useDetalhesStore((s) => s.matricula)
  const reemitirCobranca = useDetalhesStore((s) => s.reemitirCobranca)
  const fetchDetalhes = useDetalhesStore((s) => s.fetchDetalhes)
  const [reemitirLoading, setReemitirLoading] = useState(false)

  if (!matricula) return null
  const { pagamento } = matricula
  const paymentLink = linkDaCobranca(pagamento)
  const isPago = pagamento.dataPagamento !== null
  const repasseDoParceiro = matricula.repasseDoParceiro

  async function handleReemitir() {
    setReemitirLoading(true)
    try {
      await reemitirCobranca(matricula!.id)
      await fetchDetalhes(matricula!.id)
      toast.success('Cobrança reemitida!')
    } catch {
      toast.error('Erro ao reemitir cobrança.')
    } finally {
      setReemitirLoading(false)
    }
  }

  return (
    <CartaoDeSecao titulo="Dados da Cobrança" icone={<DollarOutlined />}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-gray-400 font-medium">
            Valor total
          </div>
          <div className="text-2xl font-bold text-[#1a3a5c]">{formatCurrency(pagamento.valor)}</div>
          {repasseDoParceiro > 0 && (
            <div className="text-xs font-medium text-green-600">
              Seu repasse: {formatCurrency(repasseDoParceiro)}
            </div>
          )}
        </div>
        <Tag color={isPago ? 'green' : 'red'} className="!mr-0">
          {isPago ? 'Pago' : 'Pendente'}
        </Tag>
      </div>

      <div className="mt-4 space-y-2">
        <LinhaDeValor rotulo="Meio de Pagamento" valor={pagamento.meioPagamento} />
        <LinhaDeValor rotulo="Data de Vencimento" valor={formatDate(pagamento.dataVencimento)} />
        <LinhaDeValor rotulo="Data de Pagamento" valor={formatDate(pagamento.dataPagamento)} />
      </div>

      <DetalhesDoMeioDePagamento />

      {paymentLink && (
        <Space.Compact className="w-full mt-4">
          <Input value={paymentLink} readOnly />
          <BotaoCopiar texto={paymentLink} />
        </Space.Compact>
      )}

      <AcoesDaCobranca paymentLink={paymentLink} />

      <Button
        icon={<ReloadOutlined />}
        loading={reemitirLoading}
        onClick={handleReemitir}
        block
        size="large"
        className="!mt-3"
      >
        Reemitir Cobrança
      </Button>
    </CartaoDeSecao>
  )
}

/**
 * Abrir a cobrança pelos dois lados: o link é a tela que o ALUNO paga, e "Pagar" leva o parceiro ao
 * pagamento da cobrança no painel da Hyperpay — que só existe quando a cobrança saiu por lá.
 */
function AcoesDaCobranca({ paymentLink }: { paymentLink: string | null }) {
  const matricula = useDetalhesStore((s) => s.matricula)
  const estaEmbutido = useEstaEmbutido()
  if (!matricula) return null
  const { pagamento } = matricula

  const cobrancaId =
    pagamento.provedorPagamento === 'HYPERPAY' ? pagamento.referenciaPagamento : null
  const linkDePagamentoHyperpay = cobrancaId
    ? `https://conta.pilotar.app/#/pagamentos/${cobrancaId}`
    : null

  if (!paymentLink && !linkDePagamentoHyperpay) return null

  // Embutido, abrir em aba nova tiraria o parceiro de dentro do Pilotar para cair… no Pilotar. O
  // pedido sobe para a janela de cima, que navega a si mesma sem recarregar o iframe.
  const propsDoBotaoDePagar =
    estaEmbutido && cobrancaId
      ? { onClick: () => pedirParaPagarCobrancaNoHyperpay(cobrancaId) }
      : { href: linkDePagamentoHyperpay ?? undefined, target: '_blank', rel: 'noreferrer' }

  return (
    <div className="mt-4 flex flex-col gap-3">
      {paymentLink && (
        <Button
          type="primary"
          href={paymentLink}
          target="_blank"
          rel="noreferrer"
          icon={<LinkOutlined />}
          block
          size="large"
          style={{ background: 'var(--color-accent)', borderColor: 'var(--color-accent)' }}
        >
          Abrir link de pagamento
        </Button>
      )}
      {linkDePagamentoHyperpay && (
        <Button
          type="primary"
          {...propsDoBotaoDePagar}
          icon={<DollarCircleOutlined />}
          block
          size="large"
          style={{ background: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}
        >
          Pagar com conta digital
        </Button>
      )}
    </div>
  )
}

function DetalhesDoMeioDePagamento() {
  const matricula = useDetalhesStore((s) => s.matricula)
  if (!matricula) return null
  const { pagamento } = matricula

  if (pagamento.meioPagamento === 'PIX' && pagamento.infoPix) {
    return (
      <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
        <LinhaDeValor rotulo="Valor PIX" valor={formatCurrency(pagamento.infoPix.value)} />
        <LinhaDeValor rotulo="TXID" valor={pagamento.infoPix.txid} copiavel />
        {pagamento.infoPix.e2eId && (
          <LinhaDeValor rotulo="E2E ID" valor={pagamento.infoPix.e2eId} copiavel />
        )}
        <div className="text-[11px] uppercase tracking-wide text-gray-400 font-medium pt-1">
          PIX copia e cola
        </div>
        <Space.Compact className="w-full">
          <Input value={pagamento.infoPix.pix} readOnly />
          <BotaoCopiar texto={pagamento.infoPix.pix} />
        </Space.Compact>
      </div>
    )
  }

  if (pagamento.meioPagamento === 'BOLETO' && pagamento.infoBoleto) {
    return (
      <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
        <LinhaDeValor rotulo="Vencimento" valor={formatDate(pagamento.infoBoleto.vencimento)} />
        <LinhaDeValor rotulo="Valor" valor={formatCurrency(pagamento.infoBoleto.valor)} />
        <div className="text-[11px] uppercase tracking-wide text-gray-400 font-medium pt-1">
          Linha digitável
        </div>
        <Space.Compact className="w-full">
          <Input value={pagamento.infoBoleto.linhaDigitavel} readOnly />
          <BotaoCopiar texto={pagamento.infoBoleto.linhaDigitavel} />
        </Space.Compact>
        <div className="text-[11px] uppercase tracking-wide text-gray-400 font-medium pt-1">
          Código de barras
        </div>
        <Space.Compact className="w-full">
          <Input value={pagamento.infoBoleto.codigoBarras} readOnly />
          <BotaoCopiar texto={pagamento.infoBoleto.codigoBarras} />
        </Space.Compact>
        <Space wrap>
          <Button
            href={pagamento.infoBoleto.linkPdf}
            target="_blank"
            type="link"
            icon={<LinkOutlined />}
          >
            Ver PDF
          </Button>
          <Button
            href={pagamento.infoBoleto.linkVizualizacao}
            target="_blank"
            type="link"
            icon={<LinkOutlined />}
          >
            Visualizar Boleto
          </Button>
        </Space>
      </div>
    )
  }

  if (pagamento.meioPagamento === 'CARTAO' && pagamento.infoCartao) {
    return (
      <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
        <LinhaDeValor rotulo="Bandeira" valor={pagamento.infoCartao.bandeira} />
        <LinhaDeValor rotulo="Parcelas" valor={String(pagamento.infoCartao.parcelas)} />
        <LinhaDeValor
          rotulo="Valor por parcela"
          valor={formatCurrency(pagamento.infoCartao.installmentValue)}
        />
        <LinhaDeValor rotulo="Total" valor={formatCurrency(pagamento.infoCartao.total)} />
        <LinhaDeValor
          rotulo="Últimos 4 dígitos"
          valor={pagamento.infoCartao.quatroUltimosDigitos}
        />
        <LinhaDeValor rotulo="Titular" valor={pagamento.infoCartao.nomeTitular} />
        <LinhaDeValor rotulo="CPF/CNPJ titular" valor={pagamento.infoCartao.documentoTitular} />
        <LinhaDeValor rotulo="Status" valor={pagamento.infoCartao.status} />
      </div>
    )
  }

  if (pagamento.meioPagamento === 'ISENTO') {
    return (
      <Alert
        className="!mt-4"
        type="info"
        showIcon
        description="Esta matrícula foi isenta de pagamento. Nenhuma cobrança foi gerada."
      />
    )
  }

  return null
}

/**
 * Uma ponta só da cadeia: quem está abaixo do parceiro logado, ou o pai sob quem ele mesmo vende.
 * Nunca as duas, porque o painel não expõe a cadeia inteira a ninguém.
 */
function VendedorCard() {
  const matricula = useDetalhesStore((s) => s.matricula)
  const parceiroLogadoId = useAuthStore((s) => s.parceiro?.id)
  const rotulo = useRotuloDeFilho()

  const vendedor = matricula?.parceiro
  if (!vendedor || !parceiroLogadoId) return null

  const vendidoPorFilho = vendedor.id !== parceiroLogadoId
  const parceiroExibido = vendidoPorFilho ? vendedor : vendedor.parceiroPai
  if (!parceiroExibido) return null

  return (
    <CartaoDeSecao
      titulo={vendidoPorFilho ? 'Vendido por' : 'Parceiro'}
      icone={<ShopOutlined />}
    >
      <div className="font-bold text-[#1a3a5c]">{parceiroExibido.nome}</div>
      <div className="text-xs text-gray-500">
        {vendidoPorFilho ? `Seu ${rotulo.singular}` : 'Você vende sob este parceiro'}
      </div>
    </CartaoDeSecao>
  )
}

function ProgressoAcademicoCard() {
  const matricula = useDetalhesStore((s) => s.matricula)
  if (!matricula) return null

  const modulos = [...(matricula.curso.modulos ?? [])].sort((a, b) => a.ordenacao - b.ordenacao)
  const concluidos = matricula.estatisticasEstudos.filter((e) => e.moduloCompleto).length

  return (
    <CartaoDeSecao
      titulo="Progresso Acadêmico"
      icone={<ReadOutlined />}
      acao={
        concluidos === modulos.length && modulos.length > 0 ? (
          <Tag color="green" className="!mr-0">
            Módulos concluídos
          </Tag>
        ) : (
          <Tag className="!mr-0">{`${concluidos} de ${modulos.length} módulos`}</Tag>
        )
      }
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <CaixaDeData
          rotulo="Início teórico"
          valor={formatDate(matricula.dataInicioModuloTeorico)}
        />
        <CaixaDeData rotulo="Fim teórico" valor={formatDate(matricula.dataFimModuloTeorico)} />
        <CaixaDeData
          rotulo="Validação inicial"
          valor={formatDateTime(matricula.dataValidacaoFacialInicial)}
        />
        <CaixaDeData
          rotulo="Validação final"
          valor={formatDateTime(matricula.dataValidacaoFacialFinal)}
        />
      </div>

      <TabelaDeModulos modulos={modulos} />

      <div className="border-t border-gray-100 mt-5 pt-4">
        <CertificadoAcesso />
      </div>

      <EmissaoDeCertificado />
    </CartaoDeSecao>
  )
}

function TabelaDeModulos({ modulos }: { modulos: Modulo[] }) {
  const matricula = useDetalhesStore((s) => s.matricula)
  const estatisticaMap = new Map((matricula?.estatisticasEstudos ?? []).map((e) => [e.moduloId, e]))

  return (
    <Table
      dataSource={modulos}
      rowKey="id"
      size="small"
      pagination={false}
      scroll={{ x: 'max-content' }}
      className="mt-4 [&_.ant-table-thead>tr>th]:!bg-[#f9fafb] [&_.ant-table-thead>tr>th]:!text-gray-500 [&_.ant-table-thead>tr>th]:!font-medium"
      columns={[
        {
          title: 'Módulo',
          dataIndex: 'nome',
          key: 'nome',
          render: (nome: string) => <span className="text-[#1a3a5c]">{nome}</span>,
        },
        {
          title: 'Progresso',
          key: 'progresso',
          render: (_, modulo: Modulo) => (
            <BarraDeProgresso
              progresso={Math.round(
                estatisticaMap.get(modulo.id)?.percentualProgressoMaisAvancado ?? 0
              )}
            />
          ),
        },
        {
          title: 'Status',
          key: 'concluido',
          render: (_, modulo: Modulo) =>
            estatisticaMap.get(modulo.id)?.moduloCompleto ? (
              <Tag color="green" icon={<CheckOutlined />} className="!mr-0">
                Sim
              </Tag>
            ) : (
              <Tag className="!mr-0">Não</Tag>
            ),
        },
        {
          title: 'Data conclusão',
          key: 'dataConclusao',
          render: (_, modulo: Modulo) =>
            formatDate(estatisticaMap.get(modulo.id)?.dataFinalizacaoModulo),
        },
      ]}
    />
  )
}

function BarraDeProgresso({ progresso }: { progresso: number }) {
  const concluido = progresso === 100

  return (
    <div className="flex items-center gap-2 min-w-[130px]">
      <div className="h-2 w-[90px] rounded-full bg-gray-200 overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${progresso}%`, backgroundColor: concluido ? '#10b981' : '#1a3a5c' }}
        />
      </div>
      <span className={`text-xs font-semibold ${concluido ? 'text-green-600' : 'text-[#1a3a5c]'}`}>
        {progresso}%
      </span>
    </div>
  )
}

function EmissaoDeCertificado() {
  const matricula = useDetalhesStore((s) => s.matricula)
  const definirPermissaoCertificado = useDetalhesStore((s) => s.definirPermissaoCertificado)
  const fetchDetalhes = useDetalhesStore((s) => s.fetchDetalhes)
  const [toggleLoading, setToggleLoading] = useState(false)

  if (!matricula) return null
  const permitida = matricula.permiteEmissaoCertificado

  async function handleToggle() {
    setToggleLoading(true)
    try {
      await definirPermissaoCertificado(matricula!.id, !permitida)
      await fetchDetalhes(matricula!.id)
      toast.success(permitida ? 'Emissão de certificado bloqueada.' : 'Emissão de certificado liberada.')
    } catch {
      toast.error('Erro ao atualizar a permissão de certificado.')
    } finally {
      setToggleLoading(false)
    }
  }

  return (
    <div className="border-t border-gray-100 mt-4 pt-4 flex flex-wrap items-center justify-between gap-3">
      <Text>
        Emissão de Certificado: <strong>{permitida ? 'Permitida' : 'Bloqueada'}</strong>
      </Text>
      <Button onClick={handleToggle} loading={toggleLoading} danger={permitida}>
        {permitida ? 'Bloquear' : 'Permitir'}
      </Button>
    </div>
  )
}

/**
 * Duas condições independentes liberam o PDF, e antes as duas ficavam invisíveis quando faltavam:
 * o `identificacaoCertificado` (uuid criado na conclusão do módulo teórico) e o statusDetran
 * FINALIZADO, que o backend exige em getCertificadoInfo. Aqui cada uma vira uma mensagem própria,
 * para não parecer que o link simplesmente sumiu.
 */
function CertificadoAcesso() {
  const matricula = useDetalhesStore((s) => s.matricula)
  if (!matricula) return null

  if (!matricula.identificacaoCertificado) {
    return (
      <Alert
        type="info"
        showIcon
        message="Certificado ainda não gerado"
        description="A identificação do certificado é criada quando o aluno conclui o módulo teórico. Até lá não há PDF para emitir."
      />
    )
  }

  if (matricula.statusDetran !== 'FINALIZADO') {
    return (
      <Alert
        type="warning"
        showIcon
        message="Certificado indisponível para emissão"
        description={`O PDF só é liberado depois que a matrícula é finalizada junto ao DETRAN. Status atual: ${
          STATUS_LABELS[matricula.statusDetran] ?? matricula.statusDetran
        }.`}
      />
    )
  }

  return <CertificadoLinks identificacaoCertificado={matricula.identificacaoCertificado} />
}

function CertificadoLinks({ identificacaoCertificado }: { identificacaoCertificado: string }) {
  const matricula = useDetalhesStore((s) => s.matricula)
  const pdfUrl = certificadoPdfUrl(identificacaoCertificado)
  const validacaoUrl = certificadoValidacaoUrl(identificacaoCertificado)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
        <InfoField
          label="Data emissão certificado"
          value={formatDate(matricula?.dataEmissaoCertificado)}
        />
        <InfoField label="Identificação certificado" value={identificacaoCertificado} copiavel />
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <Button
          type="primary"
          icon={<FilePdfOutlined />}
          href={pdfUrl}
          target="_blank"
          rel="noreferrer"
          className="!bg-[#ff6422] !border-[#ff6422]"
        >
          Abrir certificado (PDF)
        </Button>
        <Button
          icon={<SafetyCertificateOutlined />}
          href={validacaoUrl}
          target="_blank"
          rel="noreferrer"
        >
          Página de validação
        </Button>
        <div className="flex-1 min-w-[240px]">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 font-medium">
            Link de validação
          </div>
          <Space.Compact className="w-full">
            <Input value={validacaoUrl} readOnly />
            <BotaoCopiar texto={validacaoUrl} />
          </Space.Compact>
        </div>
      </div>
    </div>
  )
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

function CartaoDeSecao({
  titulo,
  icone,
  acao,
  children,
}: {
  titulo: string
  icone: React.ReactNode
  acao?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 font-bold text-[#1a3a5c]">
          {icone}
          {titulo}
        </div>
        {acao}
      </div>
      {children}
    </div>
  )
}

function CaixaDeData({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="border border-gray-200 rounded-lg px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-gray-400 font-medium">{rotulo}</div>
      <div className="font-bold text-[#1a3a5c] text-sm">{valor}</div>
    </div>
  )
}

function LinhaDeValor({
  rotulo,
  valor,
  copiavel,
}: {
  rotulo: string
  valor?: string | null
  copiavel?: boolean
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <Text type="secondary">{rotulo}</Text>
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-medium text-[#1a3a5c] truncate">{valor || '—'}</span>
        {copiavel && valor && <IconeCopiar texto={valor} />}
      </div>
    </div>
  )
}

function InfoField({
  label,
  value,
  copiavel,
  valorParaCopiar,
  children,
}: {
  label: string
  value?: string | null
  copiavel?: boolean
  /** Copia isto no lugar do texto exibido — ex.: CPF sem máscara, para colar em outro sistema. */
  valorParaCopiar?: string
  children?: React.ReactNode
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-400 font-medium">{label}</div>
      <div className="flex items-center justify-between gap-2">
        {children ?? (
          <span className="font-medium text-[#1a3a5c] truncate" title={value ?? undefined}>
            {value || '—'}
          </span>
        )}
        {copiavel && value && <IconeCopiar texto={valorParaCopiar ?? value} />}
      </div>
    </div>
  )
}

function IconeCopiar({ texto }: { texto: string }) {
  function handleCopy() {
    copyToClipboard(texto)
    toast.success('Copiado!')
  }

  return (
    <Tooltip title="Copiar">
      <CopyOutlined className="text-[#ff6422] cursor-pointer shrink-0" onClick={handleCopy} />
    </Tooltip>
  )
}

/**
 * Botão de copiar para valores longos que moram num Input (links, PIX, linha digitável). O estado
 * volta ao normal quando o tooltip fecha, em vez de num timer — não há o que limpar no unmount.
 */
function BotaoCopiar({ texto, rotulo = 'Copiar' }: { texto: string; rotulo?: string }) {
  const [copiado, setCopiado] = useState(false)

  return (
    <Tooltip
      title={copiado ? 'Copiado!' : rotulo}
      onOpenChange={(aberto) => {
        if (!aberto) setCopiado(false)
      }}
    >
      <Button
        icon={copiado ? <CheckOutlined /> : <CopyOutlined />}
        onClick={() => {
          copyToClipboard(texto)
          setCopiado(true)
        }}
      >
        {rotulo}
      </Button>
    </Tooltip>
  )
}
